import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

const ENDPOINT = '/api/v1/templates';
const KEY = ['templates'];

export interface ServiceTemplate {
  id: string;
  categoryTemplateId: string;
  name: string;
  photos: string[];
  durationMinutes: number;
  price: number;
  order: number;
}

export interface CategoryTemplate {
  id: string;
  name: string;
  photos: string[];
  order: number;
  services: ServiceTemplate[];
}

export interface TemplatesData {
  data: CategoryTemplate[];
}

/** A lista pronta de categorias e servicos - le qualquer conta logada. */
export function useTemplates() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => apiClient.get<TemplatesData>(ENDPOINT),
  });
}

function useTemplatesMutation<TArgs>(mutationFn: (args: TArgs) => Promise<TemplatesData>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: (dados) => queryClient.setQueryData(KEY, dados),
  });
}

export function useCreateCategoryTemplate() {
  return useTemplatesMutation((input: { name: string; photos?: string[] }) =>
    apiClient.post<TemplatesData>(`${ENDPOINT}/categories`, input),
  );
}

export function useUpdateCategoryTemplate() {
  return useTemplatesMutation(
    ({ id, input }: { id: string; input: { name?: string; photos?: string[] } }) =>
      apiClient.patch<TemplatesData>(`${ENDPOINT}/categories/${id}`, input),
  );
}

export function useDeleteCategoryTemplate() {
  return useTemplatesMutation((id: string) =>
    apiClient.delete<TemplatesData>(`${ENDPOINT}/categories/${id}`),
  );
}

export function useCreateServiceTemplate() {
  return useTemplatesMutation(
    (input: {
      categoryTemplateId: string;
      name: string;
      photos?: string[];
      durationMinutes?: number;
      price?: number;
    }) => apiClient.post<TemplatesData>(`${ENDPOINT}/services`, input),
  );
}

export function useUpdateServiceTemplate() {
  return useTemplatesMutation(
    ({
      id,
      input,
    }: {
      id: string;
      input: { name?: string; photos?: string[]; durationMinutes?: number; price?: number };
    }) => apiClient.patch<TemplatesData>(`${ENDPOINT}/services/${id}`, input),
  );
}

export function useDeleteServiceTemplate() {
  return useTemplatesMutation((id: string) =>
    apiClient.delete<TemplatesData>(`${ENDPOINT}/services/${id}`),
  );
}

export function useResetTemplates() {
  return useTemplatesMutation(() => apiClient.post<TemplatesData>(`${ENDPOINT}/reset`, {}));
}

// ---------------------------------------------------------------------------
// Banners do slide da pagina do salao
// ---------------------------------------------------------------------------

export interface BannerTemplate {
  id: string;
  image: string;
  title: string;
  subtitle: string;
  order: number;
}

export interface BannersData {
  data: BannerTemplate[];
}

const BANNERS_KEY = ['templates', 'banners'];

export function useBannerTemplates() {
  return useQuery({
    queryKey: BANNERS_KEY,
    queryFn: () => apiClient.get<BannersData>(`${ENDPOINT}/banners`),
  });
}

function useBannersMutation<TArgs>(mutationFn: (args: TArgs) => Promise<BannersData>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: (dados) => queryClient.setQueryData(BANNERS_KEY, dados),
  });
}

export function useCreateBannerTemplate() {
  return useBannersMutation((input: { image: string; title?: string; subtitle?: string }) =>
    apiClient.post<BannersData>(`${ENDPOINT}/banners`, input),
  );
}

export function useUpdateBannerTemplate() {
  return useBannersMutation(
    ({ id, input }: { id: string; input: { image?: string; title?: string; subtitle?: string } }) =>
      apiClient.patch<BannersData>(`${ENDPOINT}/banners/${id}`, input),
  );
}

export function useDeleteBannerTemplate() {
  return useBannersMutation((id: string) =>
    apiClient.delete<BannersData>(`${ENDPOINT}/banners/${id}`),
  );
}
